import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/guarantees`;

const GUARANTEES = [
  {
    title: "Our Guarantees | Colorado Web Impressions",
    body: "Every engagement is month-to-month or project-based. If we're not earning your business each month, you're not stuck. Cancel anytime, no early-termination fees, no kill clauses.",
  },
  {
    title: "Owner-direct accountability",
    body: "When you call, Chris answers. No tier-1 support, no account-manager middleman, no 'I'll have to check with the team.' If something is wrong, you talk to the person who can fix it.",
  },
  {
    title: "Transparent pricing — written down",
    body: "Every quote spells out exactly what's included, what's optional, and what each line item costs. No mystery retainers, no surprise add-ons, no 'consulting hours' that appear out of nowhere.",
  },
  {
    title: "Honest fit assessment",
    body: "If we're not the right fit for your business, we'll tell you on the first call — and we'll point you toward someone who is. We'd rather lose the sale than take on work we can't do well.",
  },
  {
    title: "We do the work in-house",
    body: "No offshoring. No whitelabel reseller agencies. No AI-generated body copy on your site. The owner does the work — that's the whole reason we exist.",
  },
  {
    title: "Plain-English reporting",
    body: "Reports are tied to calls, form fills, and booked work — not vanity metrics. Every report is written in plain English with what changed, what worked, and what we'll change next.",
  },
  {
    title: "Same-week response",
    body: "Email us during business hours and you'll get a real human reply (usually from Chris) the same day or next business morning. No support ticket queue.",
  },
  {
    title: "Your data, your accounts",
    body: "We build everything in your accounts — your domain, your hosting, your Google Business Profile, your Google Ads. If we ever part ways, you keep all of it. No hostage situations.",
  },
];

const ITEMLIST_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "Our Guarantees & Promises — Colorado Web Impressions",
      description:
        "Eight written guarantees we make to every Colorado Web Impressions client — from no-contract billing to owner-direct accountability.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      publisher: { "@id": SCHEMA_ORG_ID },
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL}#guarantees`,
      itemListElement: GUARANTEES.map((g, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: g.title,
        description: g.body,
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Guarantees", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/guarantees")({
  head: () => ({
    meta: [
        { name: "description", content: "No long-term contracts. No pricing games. Month-to-month SEO and web design from a Colorado Springs team. Here's exactly what we promise every client." },
      {
        title:
          "Our Guarantees — No Contracts, Owner-Direct | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Eight written guarantees we make to every client — no long-term contracts, owner-direct accountability, transparent pricing, in-house work.",
      },
      {
        property: "og:title",
        content: "Our Guarantees — Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "Written promises: no contracts, owner-direct, in-house, your data stays yours.",
      },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(ITEMLIST_LD) },
    ],
  }),
  component: GuaranteesPage,
});

function GuaranteesPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Our promises, in writing"
        title={
          <>
            Eight guarantees.{" "}
            <span className="text-brand-gold">No fine print.</span>
          </>
        }
        description="These are the rules we run the business by. We'd rather lose the sale than break one of them."
      />

      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <ul className="grid gap-5 sm:grid-cols-2">
            {GUARANTEES.map((g, i) => (
              <li
                key={g.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-gradient-gold text-sm font-black text-brand-black shadow-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h2 className="text-lg font-extrabold leading-snug text-foreground">
                      {g.title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                      {g.body}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-14 rounded-3xl border border-border bg-gradient-to-br from-brand-gold/10 via-card to-card p-6 sm:p-10">
            <div className="flex items-start gap-4">
              <ShieldCheck className="h-8 w-8 shrink-0 text-brand-gold-deep" />
              <div>
                <h2 className="text-2xl font-extrabold text-foreground">
                  If we ever break one of these
                </h2>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">
                  Tell Chris directly. We'll make it right — refund, redo, or
                  resolve — same week. These promises only matter if we honor
                  them when it costs us.
                </p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-gradient-gold px-5 py-3 font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                >
                  <Sparkles className="h-4 w-4" />
                  Talk to Chris directly
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
